import { OrderInvoiceType } from '@lambda/apis/src/order/types';
import { CommonHelpers, ReebeloStoreT } from '@lambda/reebelo';
import { Download } from 'lucide-react';
import { CtaLink } from '@/components/Cta/Cta';
import settings from '@/settings/index';
import i18n from '@/settings/i18n';

const t = i18n.init('orders');

interface DownloadInvoicesProps {
  invoiceUrl?: string;
  invoiceType: OrderInvoiceType;
  /** The order-level Proof of Purchase, in every market that has one. */
  isProofOfPurchase?: boolean;
}

// Where the seller issues the tax invoice, Reebelo's own document is only a
// proof of purchase.
function getDownloadText(
  invoiceType: OrderInvoiceType,
  store: ReebeloStoreT,
  isProofOfPurchase?: boolean,
): string {
  if (isProofOfPurchase) {
    return t`Proof of Purchase`;
  }

  if (invoiceType === OrderInvoiceType.BUYER_PROTECTION_FEE) {
    return t`Buyer Protection Fee Invoice`;
  }

  return invoiceType === OrderInvoiceType.STANDARD &&
    CommonHelpers.isSellerInvoiceStore(store)
    ? t`Proof of Purchase`
    : t`Invoice`;
}

export default function DownloadInvoices({
  invoiceUrl,
  invoiceType,
  isProofOfPurchase,
}: DownloadInvoicesProps) {
  const { store } = settings;

  const downloadTextNotAvailable = CommonHelpers.isSellerInvoiceStore(store)
    ? t`Proof of Purchase Not Available`
    : t`Invoice Not Available`;

  if (!invoiceUrl) {
    return <p className="text-gray-700/40">{downloadTextNotAvailable}</p>;
  }

  return (
    <div className="w-full sm:w-auto">
      <div className="hidden sm:block">
        <a
          className="flex items-center gap-1.5 text-sm font-bold"
          href={invoiceUrl}
          target="_blank"
        >
          <Download className="h-4 w-4" strokeWidth={2} />
          {getDownloadText(invoiceType, store, isProofOfPurchase)}
        </a>
      </div>
      <div className="block sm:hidden">
        <CtaLink
          variant="outlined"
          target="_blank"
          href={invoiceUrl}
          download
          className="flex w-full items-center justify-center gap-1.5 p-3 text-sm font-bold"
        >
          <Download className="h-4 w-4" strokeWidth={2} />
          {getDownloadText(invoiceType, store, isProofOfPurchase)}
        </CtaLink>
      </div>
    </div>
  );
}
